import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getWinnersData, markWinner, unmarkWinner } from '../api';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

// Utility to convert array of objects to CSV string
function arrayToCSV(data, columns) {
    if (!data || !data.length) return '';
    const header = columns.map(col => col.headerName || col.field);
    const rows = data.map(row =>
        columns.map(col => {
            let val = row[col.field];
            if (val === undefined || val === null) return '';
            // Escape quotes and commas
            val = String(val).replace(/"/g, '""');
            if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                val = '"' + val + '"';
            }
            return val;
        }).join(',')
    );
    return [header.join(','), ...rows].join('\n');
}

function downloadCSV(csv, filename) {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function Winners() {
    const { year } = useParams();
    const [tabValue, setTabValue] = useState(0); // 0 = CIG, 1 = ILG
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [updating, setUpdating] = useState({});

    const currentType = tabValue === 0 ? 'CIG' : 'ILG';

    useEffect(() => {
        loadData();
    }, [year, currentType]);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getWinnersData(year, currentType);
            setRows(res.data || []);
        } catch (err) {
            console.error(err);
            setError(`Failed to load ${currentType} winners data`);
        } finally {
            setLoading(false);
        }
    };

    // debounce the search input
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    const filteredRows = React.useMemo(() => {
        const q = debouncedSearch.toLowerCase();
        return rows.filter(r => {
            return !q || (r.schoolName || '').toLowerCase().includes(q) || String(r.schoolId || '').includes(q);
        });
    }, [rows, debouncedSearch]);

    // Sort: winners first, then by school name
    const sortedRows = React.useMemo(() => {
        const copy = [...filteredRows];
        copy.sort((a, b) => {
            // Winners sort to top
            if (a.isWinner !== b.isWinner) {
                return a.isWinner ? -1 : 1;
            }
            // Then by school name
            return (a.schoolName || '').localeCompare(b.schoolName || '');
        });
        return copy;
    }, [filteredRows]);

    const handleCheckboxChange = async (schoolId, currentValue) => {
        const newValue = !currentValue;
        setUpdating(prev => ({ ...prev, [schoolId]: true }));

        try {
            if (newValue) {
                await markWinner(year, currentType, schoolId);
                setSnackbar({ open: true, message: `School marked as ${currentType} winner`, severity: 'success' });
            } else {
                await unmarkWinner(year, currentType, schoolId);
                setSnackbar({ open: true, message: `School unmarked as ${currentType} winner`, severity: 'success' });
            }

            // Optimistically update local state
            setRows(prevRows =>
                prevRows.map(r =>
                    r.schoolId === schoolId ? { ...r, isWinner: newValue } : r
                )
            );
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: `Error updating winner status`, severity: 'error' });
        } finally {
            setUpdating(prev => ({ ...prev, [schoolId]: false }));
        }
    };

    const columns = [
        {
            field: 'schoolName',
            headerName: 'School Name',
            flex: 1,
            minWidth: 300,
            sortable: true,
        },
        {
            field: 'isWinner',
            headerName: 'Winner',
            width: 130,
            sortable: true,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    {updating[params.row.schoolId] ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Checkbox
                            checked={params.value || false}
                            onChange={() => handleCheckboxChange(params.row.schoolId, params.value)}
                            inputProps={{ 'aria-label': `Mark ${params.row.schoolName} as winner` }}
                        />
                    )}
                </Box>
            ),
        },
    ];

    const exportRows = React.useMemo(() => {
        return sortedRows
            .filter(row => row.isWinner)
            .map(row => ({
                schoolName: row.schoolName,
            }));
    }, [sortedRows]);

    const exportColumns = [
        { field: 'schoolName', headerName: 'School Name' },
    ];

    const handleExport = () => {
        const csv = arrayToCSV(exportRows, exportColumns);
        downloadCSV(csv, `${currentType.toLowerCase()}-winners-${year}.csv`);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setSearch('');
        setDebouncedSearch('');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', width: '90%', margin: '40px auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4">Grant Recipients - {year}</Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={loadData}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
                <Button variant="contained" onClick={handleExport} disabled={sortedRows.filter(r => r.isWinner).length === 0}>
                    Export Grant Recipients
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
                <Tab label="CIG" />
                <Tab label="ILG" />
            </Tabs>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Search school name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{ width: 320 }}
                />
            </Box>

            <Box sx={{ flex: 1, width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={sortedRows}
                        columns={columns}
                        getRowId={(row) => row.schoolId}
                        pageSize={25}
                        rowsPerPageOptions={[10, 25, 50]}
                        pagination
                        paginationModel={{ pageSize: 25, page: 0 }}
                        onPaginationModelChange={(newModel) => { }}
                        disableColumnMenu
                        sx={{
                            '& .MuiDataGrid-root': {
                                border: 'none',
                            },
                        }}
                    />
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
}
